import { useMemo } from 'react'
import type { CalendarEvent, RecallDetectedMeeting } from '@highlight/common'
import { isRecallDetectedMeeting } from '@shared/utils/audio-notes/AudioNoteUtils'
import { meetingMatchesCalendarEvent } from '@shared/utils/audio-notes/meeting-calendar-link'
import { useAudioNoteMeeting, useDetectedAudioNoteMeeting, useIsTakingNotes } from './use-audio-note-selectors'

/**
 * Hooks that answer "which calendar event is the active recording?" for the
 * sidepanel CTAs and ordering.
 *
 * All of them route through the one predicate in `meeting-calendar-link` rather
 * than comparing `calendarEventId` inline. That comparison used to be
 * copy-pasted across the meeting cell, the note drawer, and both upcoming
 * lists, so every timing race in the stored link (HIG-5683, HIG-5856) had to be
 * re-fixed per call site. Fix the predicate once and every consumer inherits it.
 */

/** True when the recording in progress is this calendar event's. */
export const useIsRecordingCalendarEvent = (event: CalendarEvent | null | undefined): boolean => {
  const isTakingNotes = useIsTakingNotes()
  const meeting = useAudioNoteMeeting()

  return useMemo(() => isTakingNotes && meetingMatchesCalendarEvent(meeting, event), [isTakingNotes, meeting, event])
}

/**
 * The calendar event the current meeting maps to, resolved against `events`.
 *
 * Deliberately says nothing about recording status — callers gate on their own
 * ("is a transcript running?" is `isTakingNotes`, but "has the recording
 * overrun its slot?" is `isRecording`, which includes paused). Baking one of
 * those in would just push the other caller back to resolving the event itself.
 *
 * Prefers the event already embedded on the meeting, which may not be in
 * `events` at all — a recording that ran past midnight, or an event outside the
 * caller's window.
 */
export const useCalendarEventForCurrentMeeting = (events: Array<CalendarEvent> | undefined): CalendarEvent | null => {
  const meeting = useAudioNoteMeeting()

  return useMemo(() => {
    if (!meeting) return null
    const embedded = 'calendarEvent' in meeting ? meeting.calendarEvent : undefined
    if (embedded) return embedded
    return events?.find((event) => meetingMatchesCalendarEvent(meeting, event)) ?? null
  }, [meeting, events])
}

/**
 * The Recall detection tied to this event — current or merely detected — which
 * is what a "Transcribe" CTA needs to start a recording already pointed at the
 * right meeting. Null when nothing detected maps to this event.
 */
export const useDetectedMeetingForEvent = (event: CalendarEvent | null | undefined): RecallDetectedMeeting | null => {
  const meeting = useAudioNoteMeeting()
  const detectedMeeting = useDetectedAudioNoteMeeting()

  return useMemo(() => {
    if (!event) return null
    for (const candidate of [meeting, detectedMeeting]) {
      if (candidate && isRecallDetectedMeeting(candidate) && meetingMatchesCalendarEvent(candidate, event)) {
        return candidate
      }
    }
    return null
  }, [meeting, detectedMeeting, event])
}
